/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable no-nested-ternary */
import { FC, useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { useKubeflexStore } from 'entities/kubeflex/model/useKubeflexStore';
import { useAppStore } from 'entities/user/model/store/useAppStore';
import { apiService } from 'shared/api/apiService';
import { EnPortalUserRole } from 'shared/api/contracts';
import InfoSVG from 'shared/assets/images/icons/info-small.svg';
import { saveByteArray } from 'shared/lib/saveByteArray';
import { Button } from 'shared/ui/Button/Button';
import { CustomRadio } from 'shared/ui/CustomRadio/CustomRadio';
import { Typography } from 'shared/ui/Typography/Typography';
import { ConfiguratorFormFields } from '../Calculator/model/types';
import styles from './CostCalculation.module.scss';

const { Title, Text } = Typography;

export const CostCalculation: FC<{ isMobile: boolean; isOpen: boolean; setIsOpen: (isOpen: boolean) => void }> = ({
	isMobile,
	isOpen,
	setIsOpen,
}) => {
	const { t } = useTranslation();

	const { user, currency } = useAppStore();
	const { currentConfig, configuration } = useKubeflexStore();
	const { watch } = useFormContext<ConfiguratorFormFields>();
	const daysInCurrentMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
	const [multiplier, setMultiplier] = useState(daysInCurrentMonth * 24 * 60);

	const selectedMasterNodesCount = currentConfig?.KubeflexMasterNodesCount || 1;

	const selectedMasterNodesCostPerMinute =
		(currentConfig &&
			configuration?.VmConfigurations?.find(
				config =>
					config.KubeflexMasterNodesConfigurationId === currentConfig.KubeflexMasterNodesConfigurationId,
			)?.CostPerMinute) ??
		0;

	const selectedMasterNodesConfigurationCost = currentConfig
		? selectedMasterNodesCostPerMinute * multiplier * selectedMasterNodesCount
		: 0;

	const selectedMasterNodesDiskTypeConfigurations = configuration?.DiskTypeConfigurationInfos?.filter(
		item => item.diskTypeConfigurationId === currentConfig?.KubeflexMasterNodesDiskTypeConfigurationId,
	);

	const masterDiskPrice =
		(((currentConfig?.KubeflexMasterNodesDiskSize || 0) *
			(selectedMasterNodesDiskTypeConfigurations?.[0]?.CostPerGb || 0) *
			selectedMasterNodesCount) /
			daysInCurrentMonth /
			24 /
			60) *
		multiplier;

	const size = watch(`size`) ?? 0;
	const nodes = watch(`nodes`) ?? 0;
	const type = watch(`type`);
	const isNewVirtualNetwork = watch(`virtualNetwork`) === `new`;

	const nodesPrice = (currentConfig?.CostPerMinute || 0) * multiplier * nodes;

	const selectedDiskTypeConfiguration = configuration?.DiskTypeConfigurationInfos?.find(
		item => size && item.DiskTypeId === watch(`diskType`) && item.SizeFrom <= size && item.SizeTo >= size,
	);

	const diskPrice =
		(((selectedDiskTypeConfiguration?.CostPerGb || 0) * nodes * size) / daysInCurrentMonth / 24 / 60) * multiplier;

	const discountPercent = type
		? configuration?.WlDiscountPercents?.find(item => item.ID === type)?.DiscountPercent || 0
		: 0;

	const ipTypeInfos = configuration?.IpTypeInfo?.filter(item => item.IPType === `Public`) ?? [];

	const publicIpTypeCost =
		((!isNewVirtualNetwork ? 0 : ipTypeInfos.length > 0 ? Number(ipTypeInfos[0]?.Cost) : 0) /
			daysInCurrentMonth /
			24 /
			60) *
		multiplier;

	const publicIpTypeIdForWlDisc = ipTypeInfos.length > 0 ? ipTypeInfos[0].IPTypeIDForWlDisc : null;

	const publicIpWlDiscountPercent = publicIpTypeIdForWlDisc
		? configuration?.WlDiscountPercents?.find(item => item.ID === publicIpTypeIdForWlDisc)?.DiscountPercent || 0
		: 0;

	const publicIpCostWithWlDisc =
		publicIpWlDiscountPercent !== 0
			? publicIpWlDiscountPercent > 0 && Number((Number(publicIpTypeCost) || 0).toFixed(2)) > 0
				? Number(
						(
							Number((Number(publicIpTypeCost) || 0).toFixed(2)) *
							(1 - publicIpWlDiscountPercent / 100)
						).toFixed(2),
					)
				: Number((Number(publicIpTypeCost) || 0).toFixed(2))
			: Number((Number(publicIpTypeCost) || 0).toFixed(2));

	const clusterTotalPrice = selectedMasterNodesConfigurationCost + masterDiskPrice + nodesPrice + diskPrice;
	const clusterTotalDiscount = (clusterTotalPrice * discountPercent) / 100;

	const totalPrice = clusterTotalPrice + publicIpTypeCost;
	const totalPriceWithDiscount = publicIpCostWithWlDisc + clusterTotalPrice - clusterTotalDiscount;

	const handleDownload = useCallback(async () => {
		try {
			const masterNodeConfig = configuration?.VmConfigurations?.find(
				item => item.ID === currentConfig?.KubeflexMasterNodesConfigurationId,
			);
			const response = await apiService.downloadCalculation({
				parameters: [
					{
						ID: `masternodes`,
						GroupId: `masternodes`,
						Name: t(`MASTER_NODES`),
						Cost: selectedMasterNodesConfigurationCost,
						WlDiscountPercent: discountPercent,
						ChildrenParameters: [
							{
								ID: `compute`,
								Name: `Compute`,
								Cost: 0,
							},
							{
								ID: `cpu`,
								Name: `${masterNodeConfig?.CPU} vCPU (гарант. 0%)`,
								Cost: 0,
							},
							{
								ID: `ram`,
								Name: `${masterNodeConfig?.RAM} ${t(`GB`)} RAM`,
								Cost: 0,
							},
						],
					},
					{
						ID: `masternodessystemdisk`,
						GroupId: `masternodes`,
						Name: t(`SYSTEM_DISK`),
						Cost: masterDiskPrice,
						WlDiscountPercent: discountPercent,
						ChildrenParameters: [
							{
								ID: `diskType`,
								Name: `${selectedMasterNodesDiskTypeConfigurations?.[0]?.DiskTypeName}, ${currentConfig?.KubeflexMasterNodesDiskSize} Гб`,
								Cost: 0,
							},
						],
					},
					{
						ID: `workermnodes`,
						GroupId: `workermnodes`,
						Name: t(`WORKING_UNITS`),
						Cost: nodesPrice,
						WlDiscountPercent: discountPercent,
						ChildrenParameters: [
							{
								ID: `compute`,
								Name: `Compute`,
								Cost: 0,
							},
							{
								ID: `cpu`,
								Name: `${currentConfig?.CPU} vCPU (гарант. 0%)`,
								Cost: 0,
							},
							{
								ID: `ram`,
								Name: `${currentConfig?.RAM} ${t(`GB`)} RAM`,
								Cost: 0,
							},
						],
					},
					{
						ID: `workermnodessystemdisk`,
						GroupId: `workermnodes`,
						Name: t(`SYSTEM_DISK`),
						Cost: diskPrice,
						WlDiscountPercent: discountPercent,
						ChildrenParameters: [
							{
								ID: `diskType`,
								Name: `${selectedDiskTypeConfiguration?.DiskTypeName}, ${size} ${t(`GB`)}`,
							},
						],
					},
					{
						GroupId: `externalip`,
						WlDiscountPercent: publicIpWlDiscountPercent,
						ID: `externalip`,
						Name: `Внешний IP`,
						Cost: publicIpTypeCost,
					},
				],
			});

			saveByteArray(response.FileName ?? `Calculation.xlsx`, response.buffer ?? ``);
		} catch (error) {
			console.error(error);
		}
	}, [
		configuration?.VmConfigurations,
		currentConfig?.CPU,
		currentConfig?.KubeflexMasterNodesConfigurationId,
		currentConfig?.KubeflexMasterNodesDiskSize,
		currentConfig?.RAM,
		discountPercent,
		diskPrice,
		masterDiskPrice,
		nodesPrice,
		publicIpTypeCost,
		publicIpWlDiscountPercent,
		selectedDiskTypeConfiguration?.DiskTypeName,
		selectedMasterNodesConfigurationCost,
		selectedMasterNodesDiskTypeConfigurations,
		size,
		t,
	]);

	return (
		<div className={classNames(styles.costCalculation, { [styles.costCalculationOpen]: isOpen })}>
			<div className={styles.costCalculationMobile}>
				<div className={styles.costCalculationMobileHeader}>
					<Title level={3}>
						<b>{t(`COST`)}</b>
					</Title>
					<Text>
						<b>
							{clusterTotalPrice.toFixed(2)}
							{` `}
							{currency}
						</b>
					</Text>
				</div>
				<div className={styles.costCalculationMobileButtons}>
					<Button variant='secondary' onClick={() => setIsOpen(true)} size='small'>
						{t(`SHOW_DETAILS`)}
					</Button>
				</div>
			</div>
			<div className={styles.costCalculationDesktop}>
				<div className={styles[`cost-calc-box__header`]}>
					<InfoSVG />
					<Text>{t(`COST_CALC`)}</Text>
				</div>
				<div className={styles[`cost-calc-box__items`]}>
					<div className={styles[`cost-calc-box__title`]}>
						<Title level={3} className={styles[`cost-calc-box__title-cluster`]}>
							<b>{t(`CLUSTER`)}</b>
						</Title>
						<Title
							level={3}
							className={classNames(
								styles[`cost-calc-box__item--bold`],
								styles[`cost-calc-box__title-cluster-name`],
							)}
						>
							<b>
								{watch(`name`)?.slice(0, 63)}
								{watch(`name`)?.length > 63 && `...`}
							</b>
						</Title>
					</div>
					<div className={styles[`cost-calc-box__item`]}>
						<Text>{t(`MASTER_NODES`)}</Text>
						<Text>
							{selectedMasterNodesConfigurationCost.toFixed(2)} {currency}
						</Text>
					</div>

					{/* <div className={styles[`cost-calc-box__item`]}>
					<Text>
						{t(`SYSTEM_DISK`)} ({selectedMasterNodesDiskTypeConfigurations?.[0]?.DiskTypeName ?? `Disk`})
					</Text>
					<Text>
						{masterDiskPrice.toFixed(5)}
						{` `}{currency}
					</Text>
				</div> */}
					<div className={styles[`cost-calc-box__item`]}>
						<Text>{t(`WORKING_UNITS`)}</Text>
						<Text>
							{nodesPrice.toFixed(2)} {currency}
						</Text>
					</div>
					<div className={styles[`cost-calc-box__item`]}>
						<Text>{t(`DISK_SPACE`)}</Text>
						<Text>
							{Number(diskPrice + masterDiskPrice).toFixed(2)}
							{` `}
							{currency}
						</Text>
					</div>
					{/* <div className={styles[`cost-calc-box__item`]}>
					<Text>
						{t(`SYSTEM_DISK`)} ({selectedDiskTypeConfiguration?.DiskTypeName ?? `Disk`})
					</Text>
					<Text>
						{diskPrice.toFixed(5)}
						{` `}{currency}
					</Text>
				</div> */}

					{user?.UserRole === EnPortalUserRole.WLReseller && Boolean(discountPercent) && (
						<div className={styles[`cost-calc-box__item`]}>
							<div className={styles[`cost-calc-box__item-discount-loader`]}>
								<Text>{t(`DISCOUNT`)}</Text>
								{/* <LoaderSVG /> */}
							</div>
							<Text>
								-{discountPercent}% ={` `}
								{(clusterTotalPrice - clusterTotalDiscount).toFixed(2)}
								{currency}
							</Text>
						</div>
					)}
					{isNewVirtualNetwork && (
						<div className={styles[`cost-calc-box__item__wrapper`]}>
							<div className={styles[`cost-calc-box__item`]}>
								<Text>{t(`EXTERNAL_IP`)}</Text>
								<Text>
									{publicIpTypeCost.toFixed(2)} {currency}
								</Text>
							</div>
							{user?.UserRole === EnPortalUserRole.WLReseller && Boolean(publicIpWlDiscountPercent) && (
								<div className={styles[`cost-calc-box__item`]}>
									<div className={styles[`cost-calc-box__item-discount-loader`]}>
										<Text>{t(`DISCOUNT`)}</Text>
										{/* <LoaderSVG /> */}
									</div>
									<Text>
										-{publicIpWlDiscountPercent}% ={` `}
										{publicIpCostWithWlDisc.toFixed(2)}
										{currency}
									</Text>
								</div>
							)}
						</div>
					)}
					{user?.UserRole !== EnPortalUserRole.WLReseller ? (
						<div className={styles[`cost-calc-box__item`]}>
							<Text>
								<b>{t(`COST`)}</b>
							</Text>
							<div className={styles[`cost-calc-box__item-with-loader`]}>
								{/* <LoaderSVG /> */}
								<Text>
									<b>
										{clusterTotalPrice.toFixed(2)}
										{` `}
										{currency}
									</b>
								</Text>
							</div>
						</div>
					) : discountPercent ? (
						<div className={styles[`cost-calc-box__item`]}>
							<div className={styles[`cost-calc-box__item-discount-loader`]}>
								<Text>
									<b>{t(`COST_WITH_DISCONT`)}</b>
								</Text>
								{/* <LoaderSVG /> */}
							</div>
							<div className={styles[`cost-calc-box__total-price`]}>
								<Title level={3} className={styles[`cost-calc-box__item--bold`]}>
									{Number(totalPriceWithDiscount).toFixed(2)} {currency}
								</Title>
								<Text>
									<s>{totalPrice.toFixed(2)}</s> {currency}
								</Text>
							</div>
						</div>
					) : (
						<div className={styles[`cost-calc-box__item`]}>
							<Text>
								<b>{t(`COST`)}</b>
							</Text>
							<div className={styles[`cost-calc-box__item-with-loader`]}>
								{/* <LoaderSVG /> */}
								<Text>
									<b>
										{clusterTotalPrice.toFixed(2)}
										{` `}
										{currency}
									</b>
								</Text>
							</div>
						</div>
					)}
					<div className={styles[`cost-calc-box__periods`]}>
						<CustomRadio
							id='radio_1'
							value={365 * 24 * 60}
							checked={multiplier === 365 * 24 * 60}
							labelClassName={styles[`cost-calc-box__periods-label`]}
							onChange={e => setMultiplier(parseInt(e.target.value, 10))}
						>
							{t(`YEAR`)}
						</CustomRadio>
						<CustomRadio
							id='radio_2'
							value={daysInCurrentMonth * 24 * 60}
							checked={multiplier === daysInCurrentMonth * 24 * 60}
							labelClassName={styles[`cost-calc-box__periods-label`]}
							onChange={e => setMultiplier(parseInt(e.target.value, 10))}
						>
							{t(`MONTH`)}
						</CustomRadio>
						<CustomRadio
							id='radio_3'
							value={24 * 60}
							checked={multiplier === 24 * 60}
							labelClassName={styles[`cost-calc-box__periods-label`]}
							onChange={e => setMultiplier(parseInt(e.target.value, 10))}
						>
							{t(`DAY`)}
						</CustomRadio>
						<CustomRadio
							id='radio_4'
							value={60}
							checked={multiplier === 60}
							labelClassName={styles[`cost-calc-box__periods-label`]}
							onChange={e => setMultiplier(parseInt(e.target.value, 10))}
						>
							{t(`HOURS`)}
						</CustomRadio>
					</div>

					<div className={styles.costCalculationDesktopButtons}>
						{isMobile && (
							<Button variant='secondary' size='small' onClick={() => setIsOpen(false)}>
								{t(`HIDE_DETAILS`)}
							</Button>
						)}
						<Button
							variant='secondary'
							disabled={!currentConfig}
							onClick={handleDownload}
							size={isMobile ? `small` : `default`}
						>
							{t(`DOWNLOAD_CALC`)}
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};
